"use client";

import { generateQuickId } from "@/app/_helpers/helperFunctions";
import { createSlice } from "@reduxjs/toolkit";

let initialState = {
  products: [],
  productsToReturn: [],
  productsSupplierRefund: [],
  customerProductReturn: [],
  stocksToAdjustManually: [],
  customerToRefund: "",
  productsToTransfer: [],
  recipentProduct: [],
};

const stockManagementSlice = createSlice({
  name: "stockManagement",
  initialState,
  reducers: {
    setProductsRedux(state, action) {
      // Safety fallback: if state.products is undefined (e.g. before rehydration)
      if (!Array.isArray(state.products)) {
        state.products = [];
      }
      const { item, isChecked } = action.payload;

      const productToPurchase = {
        product_name: item.products.product_name,
        branch_product_id: item.branch_product_id,
        product_id: item.product_id,
        quantity: 0,
        cost_price: 0,
        total_amount: 0,
      };

      if (isChecked) {
        state.products.push(productToPurchase);
      } else {
        state.products = state.products.filter(
          (product) => product.branch_product_id !== item.branch_product_id
        );
      }
    },

    setProductsToReturn(state, action) {
      // Safety fallback: if state.products is undefined (e.g. before rehydration)
      if (!Array.isArray(state.productsToReturn)) {
        state.productsToReturn = [];
      }
      const { item, isChecked } = action.payload;

      const productToReturn = {
        product_name: item.products.product_name,
        branch_product_id: item.branch_product_id,
        product_id: item.product_id,
        quantity: 0,
        price: 0,
        totalPrice: 0,
        reason: "",
        purchaseId: "",
      };

      if (isChecked) {
        state.productsToReturn.push(productToReturn);
      } else {
        state.productsToReturn = state.productsToReturn.filter(
          (product) => product.branch_product_id !== item.branch_product_id
        );
      }
    },

    setProductsSupplierRefunded(state, action) {
      // Safety fallback: if state.products is undefined (e.g. before rehydration)
      if (!Array.isArray(state.productsSupplierRefund)) {
        state.productsSupplierRefund = [];
      }
      const { item, isChecked } = action.payload;

      const productsSupplierRefund = {
        product_name: item.branch_products.products.product_name,
        branch_product_id: item.branch_product_id,
        product_id: item.product_id,
        quantity: 0,
        price: 0,
        totalPrice: 0,
        reason: "",
        purchaseId: "",
      };

      if (isChecked) {
        state.productsSupplierRefund.push(productsSupplierRefund);
      } else {
        state.productsSupplierRefund = state.productsSupplierRefund.filter(
          (product) => product.branch_product_id !== item.branch_product_id
        );
      }
    },

    modifyProductPurchase(state, action) {
      const { quantity, purchase_cost, total_amount, branch_product_id } =
        action.payload;

      state.products = state.products.map((prod) =>
        prod.branch_product_id === branch_product_id
          ? { ...prod, quantity, purchase_cost, total_amount }
          : prod
      );
    },

    modifyProductToReturn(state, action) {
      const {
        quantity,
        purchaseId,
        price,
        totalPrice,
        branch_product_id,
        reason,
      } = action.payload;

      state.productsToReturn = state.productsToReturn.map((prod) =>
        prod.branch_product_id === branch_product_id
          ? { ...prod, quantity, purchaseId, price, totalPrice, reason }
          : prod
      );
    },

    setCustomerProductReturn(state, action) {
      // Safety fallback: if state.products is undefined (e.g. before rehydration)
      if (!Array.isArray(state.customerProductReturn)) {
        state.customerProductReturn = [];
      }
      const { item, isChecked } = action.payload;

      const customerProductReturn = {
        product_name: item.products.product_name,
        branch_product_id: item.branch_product_id,
        product_id: item.product_id,
        quantity: 0,
        cost_price: 0,
        total_amount: 0,
      };

      if (isChecked) {
        state.customerProductReturn.push(customerProductReturn);
      } else {
        state.customerProductReturn = state.customerProductReturn.filter(
          (product) => product.branch_product_id !== item.branch_product_id
        );
      }
    },

    setStocksToAdjustManually(state, action) {
      // Safety fallback: if state.products is undefined (e.g. before rehydration)
      if (!Array.isArray(state.stocksToAdjustManually)) {
        state.stocksToAdjustManually = [];
      }
      const { item, isChecked, userId } = action.payload;

      const stocksToAdjustManually = {
        product_name: item.products.product_name,
        branch_product_id: item.branch_product_id,
        product_id: item.product_id,
        old_quantity: item.stock_quantity,
        new_quantity: 0,
        remarks: "",
        accountable_employee_id: "",
        adjusted_by: userId,
        branch_id: item.branch_id,
      };

      if (isChecked) {
        state.stocksToAdjustManually.push(stocksToAdjustManually);
      } else {
        state.stocksToAdjustManually = state.stocksToAdjustManually.filter(
          (product) => product.branch_product_id !== item.branch_product_id
        );
      }
    },

    modifyStocksToAdjustManually(state, action) {
      const {
        quantity,
        branch_product_id,
        remarks,
        accountable_employee_id,
        difference,
      } = action.payload;

      state.stocksToAdjustManually = state.stocksToAdjustManually.map((prod) =>
        prod.branch_product_id === branch_product_id
          ? {
              ...prod,
              new_quantity: quantity,
              remarks,
              accountable_employee_id,
              difference,
            }
          : prod
      );
    },

    modifyCustomerProductToRefund(state, action) {
      const { quantity, price, total, refundType, branch_product_id, reason } =
        action.payload;

      state.customerProductReturn = state.customerProductReturn.map((prod) =>
        prod.branch_product_id === branch_product_id
          ? {
              ...prod,
              quantity,
              price,
              refundType,
              total,
              reason,
            }
          : prod
      );
    },

    setCustomerToRefund(state, action) {
      state.customerToRefund = action.payload;
    },

    removeProduct(state, action) {
      const { branch_product_id } = action.payload;

      state.products = state.products.filter((prod) => {
        return prod.branch_product_id !== branch_product_id;
      });
    },

    removeCustomerProductReturn(state, action) {
      const { branch_product_id } = action.payload;

      state.customerProductReturn = state.customerProductReturn.filter(
        (prod) => {
          return prod.branch_product_id !== branch_product_id;
        }
      );
    },

    removeStocksToAdjustManually(state, action) {
      const { branch_product_id } = action.payload;

      state.stocksToAdjustManually = state.stocksToAdjustManually.filter(
        (prod) => {
          return prod.branch_product_id !== branch_product_id;
        }
      );
    },

    removeProductToReturn(state, action) {
      const { branch_product_id } = action.payload;

      state.productsToReturn = state.productsToReturn.filter((prod) => {
        return prod.branch_product_id !== branch_product_id;
      });
    },

    setProductsToTransfer(state, action) {
      if (!Array.isArray(state.productsToTransfer)) {
        state.productsToTransfer = [];
      }
      const { item, isChecked } = action.payload;

      const productsToTransfer = {
        transactionId: generateQuickId(),
        product_name: item.products.product_name,
        branch_product_id: item.branch_product_id,
        product_id: item.product_id,
        currentQuantity: item.stock_quantity,
      };

      if (isChecked) {
        state.productsToTransfer.push(productsToTransfer);
      } else {
        state.productsToTransfer = state.productsToTransfer.filter(
          (product) => product.branch_product_id !== item.branch_product_id
        );
      }
    },

    removeProductToTransfer(state, action) {
      const { branch_product_id } = action.payload;

      state.productsToTransfer = state.productsToTransfer.filter((prod) => {
        return prod.branch_product_id !== branch_product_id;
      });
    },

    modifyPRoductsToTransfer(state, action) {
      const {
        branchId,
        quantityToTrans,
        quantityToRec,
        comments,
        recipientProdId,
        transactionId,
        recipientProdName,
      } = action.payload;

      state.productsToTransfer = state.productsToTransfer.map((prod) =>
        prod.transactionId === transactionId
          ? {
              ...prod,
              branchId,
              quantityToTrans,
              quantityToRec,
              comments,
              recipientProdId,
              recipientProdName,
            }
          : prod
      );
    },

    setProductsToReturn(state, action) {
      // Safety fallback: if state.products is undefined (e.g. before rehydration)
      if (!Array.isArray(state.productsToReturn)) {
        state.productsToReturn = [];
      }
      const { item, isChecked } = action.payload;

      const productToReturn = {
        product_name: item.products.product_name,
        branch_product_id: item.branch_product_id,
        product_id: item.product_id,
        quantity: 0,
        price: 0,
        totalPrice: 0,
        reason: "",
        purchaseId: "",
      };

      if (isChecked) {
        state.productsToReturn.push(productToReturn);
      } else {
        state.productsToReturn = state.productsToReturn.filter(
          (product) => product.branch_product_id !== item.branch_product_id
        );
      }
    },

    resetStockInState(state, action) {
      return initialState;
    },
  },
});

export const {
  removeProduct,
  modifyProductPurchase,
  setProductsRedux,
  resetStockInState,
  setProductsToReturn,
  removeProductToReturn,
  modifyProductToReturn,
  setProductsSupplierRefunded,
  setCustomerProductReturn,
  setCustomerToRefund,
  removeCustomerProductReturn,
  modifyCustomerProductToRefund,
  setStocksToAdjustManually,
  setProductsToTransfer,
  modifyStocksToAdjustManually,
  removeStocksToAdjustManually,
  removeProductToTransfer,
  modifyPRoductsToTransfer,
} = stockManagementSlice.actions;

export default stockManagementSlice.reducer;
